// アプリの操作 (コマンド層の土台)。メニュー・ショートカット・(将来) MCP はここを呼ぶ。

import { useDocumentStore } from '@/stores/documentStore'
import { useUiStore } from '@/stores/uiStore'
import { decodeImageFile, isImageFile, isProjectFile, ImageTooLargeError } from '@/io/imageDecode'
import { createProjectBlob, loadProjectBlob, PROJECT_EXT } from '@/io/projectZip'
import { addFontFile, isFontAvailable, usedUserFonts, fontEmbedWarnings } from '@/text/fonts'
import { embedIssues } from '@/text/fontEmbedding'
import { importPsdBlob, exportPsdBlob, isPsdFile } from '@/io/psd'
import { copyImage, readSystemImage, getInternalClipboard, type ClipboardImage } from '@/io/clipboard'
import { maskToAlpha } from '@/brush/fill'
import { pixelStore } from '@/pixels/pixelStore'
import { rectIntersect } from '@/types/geometry'
import { encodeRgba, resampleRgba, FORMAT_EXT, type ExportFormat } from '@/io/imageEncode'
import { downloadBlob, pickFiles, sanitizeFilename } from '@/io/download'
import { exportPixels, renderLayerAlone } from './engine'
import { toast } from '@/composables/useToast'
import { t } from '@/i18n'
import { MAX_DOCUMENT_DIMENSION } from '@/types/document'

function baseName(name: string): string {
  return name.replace(/\.[^.]+$/, '')
}

export interface OpenOptions {
  /** 未保存のときに確認ダイアログを出すか (MCP からは出さず、呼び出し側が先に判断する) */
  confirmReplace?: boolean
  /** 失敗をトーストではなく例外にする */
  rethrow?: boolean
}

/**
 * テキストレイヤーが使っている書体のうち、この PC で使えないものを警告する。
 * 同梱フォントとプロジェクトに埋め込まれたフォント以外は実体を持ち回れないので、
 * 別の PC で開くと黙って別の書体で描かれてしまう。それを黙らせない。
 */
function warnMissingFonts(doc: import('@/types/document').Document): void {
  const missing = new Set<string>()
  for (const l of Object.values(doc.layers)) {
    if (l.kind === 'text' && !isFontAvailable(l.style.fontFamily)) missing.add(l.style.fontFamily)
  }
  if (missing.size) toast.warn(t('toast.fontsMissing', { fonts: [...missing].join(', ') }), 12000)
}

export async function openFiles(files: File[], opts: OpenOptions = {}): Promise<void> {
  const docStore = useDocumentStore()
  const ui = useUiStore()
  const confirmReplace = opts.confirmReplace !== false
  for (const file of files) {
    try {
      if (isProjectFile(file)) {
        if (docStore.isDirty && confirmReplace && !confirm(t('dialog.close.confirm'))) return
        const loaded = await loadProjectBlob(file)
        // 埋め込まれたフォントは文書を差し替える前に登録する (最初の描画から正しい字形にするため)
        for (const f of loaded.fonts) {
          try {
            await addFontFile(f.data.slice(0).buffer, f.filename, f.family)
          } catch (e) {
            console.error(e)
          }
        }
        docStore.replaceDocument(loaded.document, loaded.surfaces)
        ui.fitToViewport(loaded.document.meta.width, loaded.document.meta.height)
        toast.success(t('toast.opened', { name: file.name }))
        warnMissingFonts(loaded.document)
      } else if (isPsdFile(file)) {
        if (docStore.isDirty && confirmReplace && !confirm(t('dialog.close.confirm'))) return
        const imported = await importPsdBlob(file, baseName(file.name))
        docStore.replaceDocument(imported.document, imported.surfaces)
        ui.fitToViewport(imported.document.meta.width, imported.document.meta.height)
        toast.success(t('toast.opened', { name: file.name }))
        if (imported.report.warnings.length) {
          const kinds = [...new Set(imported.report.warnings.map(w => w.kind))].join(', ')
          toast.warn(t('toast.psdWarnings', { n: imported.report.warnings.length, kinds }), 8000)
        }
        warnMissingFonts(imported.document)
      } else if (isImageFile(file)) {
        const img = await decodeImageFile(file)
        if (!docStore.hasDocument) {
          docStore.newDocument({ name: baseName(file.name), width: img.width, height: img.height, background: 'transparent' })
          docStore.addRasterLayerFromRgba(baseName(file.name), img.width, img.height, img.rgba, { x: 0, y: 0 }, 'history.openImage')
          docStore.markSaved() // 開いただけの状態は未保存扱いにしない
          ui.fitToViewport(img.width, img.height)
        } else {
          const d = docStore.doc!
          const offset = { x: Math.round((d.meta.width - img.width) / 2), y: Math.round((d.meta.height - img.height) / 2) }
          docStore.addRasterLayerFromRgba(baseName(file.name), img.width, img.height, img.rgba, offset, 'history.placeImage')
        }
        toast.success(t('toast.opened', { name: file.name }))
      } else {
        if (opts.rethrow) throw new Error(t('toast.unsupportedFile', { name: file.name }))
        toast.warn(t('toast.unsupportedFile', { name: file.name }))
      }
    } catch (e) {
      if (opts.rethrow) throw e
      if (e instanceof ImageTooLargeError) toast.error(t('toast.tooLarge', { max: MAX_DOCUMENT_DIMENSION, w: e.width, h: e.height }))
      else toast.error(t('toast.openFailed', { reason: e instanceof Error ? e.message : String(e) }))
      console.error(e)
    }
  }
}

export async function openFilePicker(): Promise<void> {
  const files = await pickFiles('image/*,.bpe.zip,.zip,.psd,.psb', true)
  if (files.length) await openFiles(files)
}

export async function saveProject(): Promise<void> {
  const docStore = useDocumentStore()
  const doc = docStore.doc
  if (!doc) return
  try {
    const fonts = usedUserFonts(doc)
    const { blob, document: saved } = await createProjectBlob(doc, __APP_VERSION__, fonts)
    downloadBlob(blob, `${sanitizeFilename(saved.meta.name)}${PROJECT_EXT}`)
    // 書き出した時点の文書と現在の文書が同じなら保存済みにする
    if (docStore.doc === saved) docStore.markSaved()
    toast.success(fonts.length ? t('toast.savedWithFonts', { n: fonts.length }) : t('toast.saved'))
    // 保存は済ませたうえで、フォント自身が同梱を許可していないものがあれば知らせる
    const issues = embedIssues(fonts)
    if (issues.length) {
      fontEmbedWarnings.value = issues
      useUiStore().dialog = 'fontEmbed'
    }
  } catch (e) {
    toast.error(t('toast.saveFailed', { reason: e instanceof Error ? e.message : String(e) }))
    console.error(e)
  }
}

export interface ExportOptions {
  format: ExportFormat
  quality: number
  scale: number
}

/** 書き出し結果を Blob として作る (ダイアログの推定サイズ / プレビュー用) */
export async function renderExportBlob(opts: ExportOptions): Promise<{ blob: Blob; width: number; height: number } | null> {
  const docStore = useDocumentStore()
  if (!docStore.doc) return null
  let px = exportPixels()
  if (opts.scale !== 1) px = resampleRgba(px.width, px.height, px.rgba, opts.scale)
  const blob = await encodeRgba(px.width, px.height, px.rgba, { format: opts.format, quality: opts.quality })
  return { blob, width: px.width, height: px.height }
}

export async function exportImage(opts: ExportOptions): Promise<void> {
  const docStore = useDocumentStore()
  const doc = docStore.doc
  if (!doc) return
  try {
    let px = exportPixels()
    if (opts.scale !== 1) px = resampleRgba(px.width, px.height, px.rgba, opts.scale)
    const blob = await encodeRgba(px.width, px.height, px.rgba, { format: opts.format, quality: opts.quality })
    const name = `${sanitizeFilename(doc.meta.name)}.${FORMAT_EXT[opts.format]}`
    downloadBlob(blob, name)
    toast.success(t('toast.exported', { name }))
  } catch (e) {
    toast.error(t('toast.saveFailed', { reason: e instanceof Error ? e.message : String(e) }))
    console.error(e)
  }
}

/** 最上位の各レイヤーを PNG にして ZIP でダウンロード */
export async function exportLayersZip(): Promise<number> {
  const docStore = useDocumentStore()
  const doc = docStore.doc
  if (!doc) return 0
  const { zipSync } = await import('fflate')
  const files: Record<string, Uint8Array> = {}
  let n = 0
  for (const id of doc.rootChildren) {
    const l = doc.layers[id]
    if (!l || l.kind === 'adjustment') continue
    const px = renderLayerAlone(id)
    if (!px) continue
    const blob = await encodeRgba(px.width, px.height, px.rgba, { format: 'png', quality: 1 })
    n++
    files[`${String(n).padStart(2, '0')}_${sanitizeFilename(l.name) || 'layer'}.png`] = new Uint8Array(await blob.arrayBuffer())
  }
  if (!n) return 0
  const bytes = zipSync(files, { level: 0 })
  downloadBlob(new Blob([bytes as BlobPart], { type: 'application/zip' }), `${sanitizeFilename(doc.meta.name)}-layers.zip`)
  toast.success(t('toast.exportedLayers', { n }))
  return n
}

export async function exportPsd(): Promise<void> {
  const docStore = useDocumentStore()
  const doc = docStore.doc
  if (!doc) return
  try {
    const px = exportPixels()
    const { blob, report } = await exportPsdBlob(doc, px)
    downloadBlob(blob, `${sanitizeFilename(doc.meta.name)}.psd`)
    toast.success(t('toast.exported', { name: `${doc.meta.name}.psd` }))
    if (report.warnings.length) toast.warn(t('toast.psdWarnings', { n: report.warnings.length, kinds: [...new Set(report.warnings.map(w => w.kind))].join(', ') }), 8000)
  } catch (e) {
    toast.error(t('toast.saveFailed', { reason: e instanceof Error ? e.message : String(e) }))
    console.error(e)
  }
}

/** 選択範囲 (なければレイヤー全体) のアクティブレイヤー画素を取り出す */
function grabActivePixels(): ClipboardImage | null {
  const docStore = useDocumentStore()
  const doc = docStore.doc
  const id = doc?.activeLayerIds[0]
  const layer = id ? docStore.rasterLayer(id) : undefined
  if (!doc || !layer) return null
  const surface = pixelStore.get(layer.pixelId)
  const b = surface?.contentBounds()
  if (!surface || !b) return null
  const layerRect = { x: b.x + layer.offset.x, y: b.y + layer.offset.y, w: b.w, h: b.h }
  const mask = docStore.selectionMask()
  const rect = mask ? rectIntersect(layerRect, mask.rect) : layerRect
  if (!rect) return null
  const rgba = surface.readRect({ x: rect.x - layer.offset.x, y: rect.y - layer.offset.y, w: rect.w, h: rect.h })
  if (mask) {
    const alpha = maskToAlpha(mask, rect)
    for (let i = 0; i < rect.w * rect.h; i++) rgba[i * 4 + 3] = Math.round((rgba[i * 4 + 3]! * alpha[i]!) / 255)
  }
  return { width: rect.w, height: rect.h, rgba, rect }
}

export async function copySelection(): Promise<boolean> {
  const img = grabActivePixels()
  if (!img) return false
  const where = await copyImage(img)
  toast.info(t(where === 'system' ? 'toast.copied' : 'toast.copiedInternal'), 1500)
  return true
}

export async function cutSelection(): Promise<void> {
  if (await copySelection()) useDocumentStore().clearSelectedPixels()
}

/** クリップボードの画像を新規レイヤーとして貼り付ける (コピー元の位置 or 中央) */
export async function pasteAsLayer(external?: ClipboardImage | null): Promise<void> {
  const docStore = useDocumentStore()
  const ui = useUiStore()
  // システムクリップボードの画像が内部バッファと同じ内容なら、コピー元の位置情報を持つ内部バッファを使う
  let img = external ?? null
  if (!img) {
    const sys = await readSystemImage()
    const internal = getInternalClipboard()
    img = sys && internal && sameImage(sys, internal) ? internal : (sys ?? internal)
  }
  if (!img) return
  if (!docStore.hasDocument) {
    docStore.newDocument({ name: t('app.untitled'), width: img.width, height: img.height, background: 'transparent' })
    docStore.addRasterLayerFromRgba(t('layer.pasted'), img.width, img.height, img.rgba, { x: 0, y: 0 }, 'history.paste')
    ui.fitToViewport(img.width, img.height)
    return
  }
  const d = docStore.doc!
  const offset = img.rect ? { x: img.rect.x, y: img.rect.y } : { x: Math.round((d.meta.width - img.width) / 2), y: Math.round((d.meta.height - img.height) / 2) }
  docStore.addRasterLayerFromRgba(t('layer.pasted'), img.width, img.height, img.rgba, offset, 'history.paste')
}

function sameImage(a: ClipboardImage, b: ClipboardImage): boolean {
  if (a.width !== b.width || a.height !== b.height || a.rgba.length !== b.rgba.length) return false
  for (let i = 0; i < a.rgba.length; i++) if (a.rgba[i] !== b.rgba[i]) return false
  return true
}

export function closeDocument(): void {
  const docStore = useDocumentStore()
  if (!docStore.hasDocument) return
  if (docStore.isDirty && !confirm(t('dialog.close.confirm'))) return
  docStore.closeDocument()
}

export function undo(): void {
  const docStore = useDocumentStore()
  const e = docStore.undo()
  if (e) toast.info(t('toast.undo', { label: t(e.label.key as never) }), 1200)
}

export function redo(): void {
  const docStore = useDocumentStore()
  const e = docStore.redo()
  if (e) toast.info(t('toast.redo', { label: t(e.label.key as never) }), 1200)
}

export function zoomFit(): void {
  const docStore = useDocumentStore()
  const ui = useUiStore()
  if (docStore.doc) ui.fitToViewport(docStore.doc.meta.width, docStore.doc.meta.height)
}

export function zoomActual(): void {
  const docStore = useDocumentStore()
  const ui = useUiStore()
  if (docStore.doc) ui.centerAt(1, docStore.doc.meta.width, docStore.doc.meta.height)
}

export function deleteActiveLayer(): void {
  const docStore = useDocumentStore()
  const ids = docStore.doc?.activeLayerIds ?? []
  if (ids.length) docStore.removeLayers(ids)
}

/** Delete キー: 選択範囲があれば消去、なければレイヤー削除 */
export function deleteOrClear(): void {
  const docStore = useDocumentStore()
  if (docStore.hasSelection) {
    docStore.clearSelectedPixels()
    return
  }
  deleteActiveLayer()
}

export function openSelectionModify(kind: 'feather' | 'expand' | 'contract' | 'border'): void {
  const ui = useUiStore()
  ui.dialogArg = kind
  ui.dialog = 'selectionModify'
}

export function groupActiveLayers(): void {
  const docStore = useDocumentStore()
  const ids = docStore.doc?.activeLayerIds ?? []
  if (ids.length) docStore.groupLayers(ids)
}

export function toggleClipActiveLayer(): void {
  const docStore = useDocumentStore()
  const id = docStore.doc?.activeLayerIds[0]
  if (id) docStore.toggleClip(id)
}

/** 自由変形モードに入る (実体は CanvasView が uiStore.transformRequest を監視して開始する) */
export function startFreeTransform(): void {
  const docStore = useDocumentStore()
  const ui = useUiStore()
  const l = docStore.activeLayer
  if (!l || (l.kind !== 'raster' && l.kind !== 'shape' && l.kind !== 'smart')) return
  ui.transformRequest++
}
